import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaChartLine, FaSearch, FaSitemap } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Accounts.css';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [subTypeFilter, setSubTypeFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    accountName: '',
    accountType: 'asset',
    subType: 'sundry_debtor',
    description: '',
    parentAccount: '',
    isParent: false,
    accountNumber: '',
    bankName: '',
    routingNumber: '',
    openingBalance: 0,
    currency: 'INR',
    isActive: true
  });

  const accountTypes = [
    { value: 'asset', label: 'Asset', color: '#28a745' },
    { value: 'liability', label: 'Liability', color: '#dc3545' },
    { value: 'equity', label: 'Equity', color: '#007bff' },
    { value: 'revenue', label: 'Revenue', color: '#17a2b8' },
    { value: 'expense', label: 'Expense', color: '#ffc107' }

  ];

  const subTypes = {
    asset: [
      { value: 'sundry_debtor', label: 'Sundry Debtor' },
      { value: 'fixed_asset', label: 'Fixed Asset' },
      { value: 'intangible_asset', label: 'Intangible Asset' },
      { value: 'other_asset', label: 'Other Asset' }
    ],
    liability: [
      { value: 'current_liability', label: 'Current Liability' },
      { value: 'long_term_liability', label: 'Long Term Liability' },
      { value: 'other_liability', label: 'Other Liability' }
    ],
    equity: [
      { value: 'owner_equity', label: 'Owner Equity' },
      { value: 'retained_earnings', label: 'Retained Earnings' },
      { value: 'other_equity', label: 'Other Equity' }
    ],
    revenue: [
      { value: 'sales_revenue', label: 'Sales Revenue' },
      { value: 'service_revenue', label: 'Service Revenue' },
      { value: 'other_revenue', label: 'Other Revenue' }
    ],
    expense: [
      { value: 'operating_expense', label: 'Operating Expense' },
      { value: 'administrative_expense', label: 'Administrative Expense' },
      { value: 'other_expense', label: 'Other Expense' }
    ]
  };

  const currencies = [
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' }
  ];

  const getCurrencySymbol = (code) => {
    switch ((code || '').toUpperCase()) {
      case 'INR': return '₹';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CAD': return 'C$';
      default: return '';
    }
  };

  const formatAmount = (amount, code) => {
    const symbol = getCurrencySymbol(code);
    const value = typeof amount === 'number' ? amount.toFixed(2) : '0.00';
    return `${symbol}${value}`;
  };

  const getSubTypeLabel = (subTypeValue) => {
    if (!subTypeValue) return '';
    const allSubTypes = Object.values(subTypes).flat();
    const found = allSubTypes.find(st => st.value === subTypeValue);
    return found ? found.label : subTypeValue.replace(/_/g, ' ');
  };

  useEffect(() => {
    loadAccounts();
    loadParentAccounts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, accountTypeFilter, subTypeFilter, isActiveFilter, viewMode]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (accountTypeFilter) params.append('accountType', accountTypeFilter);
      if (subTypeFilter) params.append('subType', subTypeFilter);
      if (isActiveFilter !== '') params.append('isActive', isActiveFilter);
      if (search) params.append('search', search);

      const response = await api.get(`/api/accounts?${params.toString()}`);
      setAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadParentAccounts = async () => {
    try {
      const response = await api.get('/api/accounts?isParent=true');
      setParentAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error loading parent accounts:', err);
    }
  };

  const loadChartOfAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/accounts/chart');
      setAccounts(response.data.chartOfAccounts || []);
    } catch (err) {
      console.error('Error loading chart of accounts:', err);
      setError('Failed to load chart of accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await api.delete(`/api/accounts/${id}`);
        setAccounts(accounts.filter(a => a._id !== id));
        loadParentAccounts(); // Refresh parent accounts list
      } catch (err) {
        console.error('Error deleting account:', err);
        setError('Failed to delete account');
      }
    }
  };

  const openAddModal = () => {
    setEditingAccount(null);
    setFormData({
      accountName: '',
      accountType: 'asset',
      subType: 'sundry_debtor',
      description: '',
      parentAccount: '',
      isParent: false,
      accountNumber: '',
      bankName: '',
      routingNumber: '',
      openingBalance: 0,
      currency: 'INR',
      isActive: true
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      accountName: account.accountName || '',
      accountType: account.accountType || 'asset',
      subType: account.subType || 'sundry_debtor',
      description: account.description || '',
      parentAccount: account.parentAccount?._id || '',
      isParent: account.isParent || false,
      accountNumber: account.accountNumber || '',
      bankName: account.bankName || '',
      routingNumber: account.routingNumber || '',
      openingBalance: account.openingBalance || 0,
      currency: account.currency || 'INR',
      isActive: account.isActive !== undefined ? account.isActive : true
    });
    setShowModal(true);
    setError('');
  };

  const openViewModal = (account) => {
    setViewingAccount(account);
  };

  const openLedgerForAccount = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setViewingAccount(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Update subType when accountType changes
    if (name === 'accountType') {
      const firstSubType = subTypes[value]?.[0]?.value || '';
      setFormData(prev => ({
        ...prev,
        subType: firstSubType
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.accountName || !formData.accountType || !formData.subType) {
        throw new Error('Please fill in all required fields');
      }

      if (editingAccount) {
        const response = await api.put(`/api/accounts/${editingAccount._id}`, formData);
        setAccounts(accounts.map(a => a._id === editingAccount._id ? response.data.account : a));
      } else {
        const response = await api.post('/api/accounts', formData);
        setAccounts([...accounts, response.data.account]);
      }

      loadParentAccounts(); // Refresh parent accounts list
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(a =>
    a.accountName.toLowerCase().includes(search.toLowerCase()) ||
    (a.accountCode && a.accountCode.toLowerCase().includes(search.toLowerCase())) ||
    (a.description && a.description.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(pageStart, pageEnd);

  const renderAccountTree = (accounts, level = 0) => {
    return accounts.map(account => (
      <div key={account._id} className="account-tree-item" style={{ marginLeft: `${level * 20}px` }}>
        <div className="account-tree-content">
          <div className="account-tree-info">
            <span className="account-code">{account.accountCode}</span>
            <span className="account-name">{account.accountName}</span>
            <span className={`account-type-badge ${account.accountType}`}>
              {account.accountType}
            </span>
            <span className="account-balance">
              {formatAmount(account.currentBalance || 0, account.currency)}
            </span>
          </div>
          <div className="account-tree-actions">
            <button
              onClick={() => openViewModal(account)}
              className="action-btn view"
              title="View Account"
            >
              <FaEye />
            </button>
            <button
              onClick={() => openEditModal(account)}
              className="action-btn edit"
              title="Edit Account"
            >
              <FaEdit />
            </button>
            <button
              onClick={() => handleDelete(account._id)}
              className="action-btn delete"
              title="Delete Account"
            >
              <FaTrash />
            </button>
          </div>
        </div>
        {account.children && account.children.length > 0 && (
          <div className="account-tree-children">
            {renderAccountTree(account.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (loading && accounts.length === 0) return <Loading />;

  return (
    <div className="accounts-container">
      <div className="accounts-header">
        <div>
          <h1 className="accounts-title">Account Management</h1>
          <p className="accounts-subtitle">Create, edit, and manage chart of accounts</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <FaSearch /> Table View
            </button>
            <button
              className={`toggle-btn ${viewMode === 'chart' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('chart');
                loadChartOfAccounts();
              }}
            >
              <FaSitemap /> Chart View
            </button>
          </div>
          <button className="add-account-btn" onClick={openAddModal}>
            <FaPlus /> Add New Account
          </button>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search accounts by name, code, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="filter-row">
          <select
            value={accountTypeFilter}
            onChange={e => setAccountTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            {accountTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <select
            value={subTypeFilter}
            onChange={e => setSubTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Sub Types</option>
            {Object.values(subTypes).flat().map(subType => (
              <option key={subType.value} value={subType.value}>
                {subType.label}
              </option>
            ))}
          </select>

          <select
            value={isActiveFilter}
            onChange={e => setIsActiveFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          <button onClick={loadAccounts} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="accounts-table-wrapper">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              Showing {filteredAccounts.length === 0 ? 0 : (pageStart + 1)}-
              {Math.min(filteredAccounts.length, pageEnd)} of {filteredAccounts.length}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
              <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
            </div>
          </div>
          <table className="accounts-table">
            <thead>
              <tr>
                <th>Account Code</th>
                <th>Account Name</th>
                <th>Type</th>
                <th>Sub Type</th>
                <th>Parent Account</th>
                <th>Current Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center' }}>No accounts found.</td>
                </tr>
              ) : (
                paginatedAccounts.map(account => (
                  <tr key={account._id}>
                    <td>
                      <div className="account-code-cell">
                        <FaChartLine className="account-icon" />
                        <span>{account.accountCode}</span>
                      </div>
                    </td>
                    <td>
                      <div className="account-name-cell">
                        <span className="account-name">{account.accountName}</span>
                        {account.description && (
                          <span className="account-description">{account.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`account-type-badge ${account.accountType}`}>
                        {account.accountType}
                      </span>
                    </td>
                    <td>
                      <span className="account-subtype">
                        {getSubTypeLabel(account.subType)}
                      </span>
                    </td>
                    <td>{account.parentAccount?.accountName || 'N/A'}</td>
                    <td>
                      <span className={`account-balance ${account.currentBalance >= 0 ? 'positive' : 'negative'}`}>
                        {formatAmount(account.currentBalance || 0, account.currency)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${account.isActive ? 'active' : 'inactive'}`}>
                        {account.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-row">
                        <button
                          onClick={() => openViewModal(account)}
                          className="action-btn view"
                          title="View Account"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => openEditModal(account)}
                          className="action-btn edit"
                          title="Edit Account"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(account._id)}
                          className="action-btn delete"
                          title="Delete Account"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="chart-of-accounts">
          <div className="chart-header">
            <h3>Chart of Accounts</h3>
            <p>Hierarchical view of all accounts</p>
          </div>
          <div className="account-tree">
            {renderAccountTree(filteredAccounts)}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAccount ? 'Edit Account' : 'Add New Account'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="account-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="accountName">Account Name *</label>
                    <input
                      type="text"
                      id="accountName"
                      name="accountName"
                      value={formData.accountName}
                      onChange={handleInputChange}
                      placeholder="Enter account name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="accountType">Account Type *</label>
                    <select
                      id="accountType"
                      name="accountType"
                      value={formData.accountType}
                      onChange={handleInputChange}
                      required
                    >
                      {accountTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subType">Sub Type *</label>
                    <select
                      id="subType"
                      name="subType"
                      value={formData.subType}
                      onChange={handleInputChange}
                      required
                    >
                      {subTypes[formData.accountType]?.map(subType => (
                        <option key={subType.value} value={subType.value}>
                          {subType.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="parentAccount">Parent Account</label>
                    <select
                      id="parentAccount"
                      name="parentAccount"
                      value={formData.parentAccount}
                      onChange={handleInputChange}
                    >
                      <option value="">No Parent Account</option>
                      {parentAccounts.map(account => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter account description"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Financial Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="openingBalance">Opening Balance</label>
                    <input
                      type="number"
                      id="openingBalance"
                      name="openingBalance"
                      value={formData.openingBalance}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="currency">Currency</label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      {currencies.map(currency => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Bank Information (Optional)</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="accountNumber">Account Number</label>
                    <input
                      type="text"
                      id="accountNumber"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      placeholder="Bank account number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bankName">Bank Name</label>
                    <input
                      type="text"
                      id="bankName"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="Bank name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="routingNumber">Routing Number</label>
                  <input
                    type="text"
                    id="routingNumber"
                    name="routingNumber"
                    value={formData.routingNumber}
                    onChange={handleInputChange}
                    placeholder="Bank routing number"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Settings</h3>
                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isParent"
                        checked={formData.isParent}
                        onChange={handleInputChange}
                      />
                      Is Parent Account
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingAccount ? 'Update Account' : 'Add Account')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingAccount && (
        <div className="modal-overlay" onClick={() => setViewingAccount(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Account Details - {viewingAccount.accountCode}</h2>
              <button className="modal-close" onClick={() => setViewingAccount(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="account-view-content">
              <div className="account-info-section">
                <div className="detail-group">
                  <h3>Basic Information</h3>
                  <div className="detail-item">
                    <label>Account Code:</label>
                    <span>{viewingAccount.accountCode}</span>
                  </div>
                  <div className="detail-item">
                    <label>Account Name:</label>
                    <span>{viewingAccount.accountName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Account Type:</label>
                    <span className={`account-type-badge ${viewingAccount.accountType}`}>
                      {viewingAccount.accountType}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Sub Type:</label>
                    <span>{getSubTypeLabel(viewingAccount.subType)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Parent Account:</label>
                    <span>{viewingAccount.parentAccount?.accountName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingAccount.isActive ? 'active' : 'inactive'}`}>
                      {viewingAccount.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Financial Information</h3>
                  <div className="detail-item">
                    <label>Opening Balance:</label>
                    <span>${viewingAccount.openingBalance?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Current Balance:</label>
                    <span className={`account-balance ${viewingAccount.currentBalance >= 0 ? 'positive' : 'negative'}`}>
                      ${viewingAccount.currentBalance?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Currency:</label>
                    <span>{viewingAccount.currency || 'USD'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Is Parent Account:</label>
                    <span>{viewingAccount.isParent ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {viewingAccount.bankName && (
                <div className="bank-section">
                  <h3>Bank Information</h3>
                  <div className="bank-details">
                    <div className="detail-item">
                      <label>Bank Name:</label>
                      <span>{viewingAccount.bankName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Account Number:</label>
                      <span>{viewingAccount.accountNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Routing Number:</label>
                      <span>{viewingAccount.routingNumber || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {viewingAccount.description && (
                <div className="description-section">
                  <h3>Description</h3>
                  <p>{viewingAccount.description}</p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                <button className="btn-primary" onClick={openLedgerForAccount}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={(didChange) => {
          setShowLedgerModal(false);
          if (didChange) {
            // No local list here to refresh; ledger list page will show it
          }
        }}
        defaultAccountId={viewingAccount?._id}
        defaultDescription={viewingAccount ? `Manual adjustment for ${viewingAccount.accountName}` : ''}
      />
    </div>
  );
};

export default Accounts;
